import { Col, Form, Input, InputNumber, Row } from 'antd';
import type { Store } from 'antd/es/form/interface';
import { useContext } from 'react';

import { SearchBox } from 'app/components/fields/SearchBox';
import { Option, Select } from 'app/components/fields/Select';
import { FormSection } from 'app/components/forms/FormSection';
import { Card } from 'app/components/layout/Card';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useAdminUser } from 'app/hooks/data/useAdminUser';
import { useCountries } from 'app/hooks/data/useCountries';
import { useHotelGroups } from 'app/hooks/data/useHotelGroups';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';

import { isSubCityRequired } from '../NewHotel/utils';

import { Communication } from './Communication';

type Props = {
  values: Hotel;
  setFieldsValue: (value: Store) => void;
  originalBillingEmails: string[] | undefined;
};

const RULES = [{ required: true }];

export const BasicInfo = ({
  values,
  setFieldsValue,
  originalBillingEmails,
}: Props) => {
  const { mode, isViewMode } = useContext(ViewEditModeContext);

  const { data: admins } = useAdminUser();
  const { data: countries } = useCountries();
  const { data: hotelGroups } = useHotelGroups();

  const isDisabled = isViewMode;

  return (
    <Card title="Basic information" formOnly>
      <FormSection title="Identity">
        <Form.Item label="Hotel name" name="name" rules={RULES}>
          <Input disabled={isDisabled} />
        </Form.Item>
        <Form.Item label="Slug" name="slug">
          <Input disabled={true} />
        </Form.Item>
        <Row>
          <Col span={8}>
            <Form.Item label="Stars" name="stars" rules={RULES}>
              <InputNumber
                min={0}
                max={5}
                disabled={isDisabled}
                placeholder={isDisabled ? undefined : '1 - 5'}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Room count" name="roomCount" rules={RULES}>
              <InputNumber
                min={0}
                step={10}
                disabled={isDisabled}
                placeholder={isDisabled ? undefined : '10 - 500'}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>
      <FormSection title="Address">
        <Row>
          <Col span={16}>
            <Form.Item
              label="Street"
              name={['address', 'street']}
              rules={RULES}
            >
              <Input
                disabled={isDisabled}
                placeholder={isDisabled ? undefined : 'Street name and number'}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Zip" name={['address', 'zipCode']} rules={RULES}>
              <Input
                disabled={isDisabled}
                placeholder={isDisabled ? undefined : 'Postal code'}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item label="City" name={['address', 'city']} rules={RULES}>
              <Input disabled={isDisabled} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Country"
              name={['address', 'countryId']}
              rules={RULES}
            >
              <Select
                disabled={isDisabled}
                placeholder={isDisabled ? undefined : 'France, UK, ...'}
              >
                {countries?.map((country) => (
                  <Option value={country.id} key={country.id}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item
              label="Station"
              name="subway"
              // This rule should never cause an error, because it accepts any value from the Input
              // We only add it because if there is no rule, we can't edit the input's value
              // https://staycation-tech.atlassian.net/browse/P1-1044
              // TODO: remove this line when we upgrade antd to a version >= 4.17.0 (this version solves the bug).
              rules={[{ type: 'string', message: 'Please enter a text' }]}
            >
              <Input
                disabled={isDisabled}
                placeholder={isDisabled ? undefined : 'Metro station'}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Lat"
              name={['location', 'coords', 'lat']}
              rules={RULES}
            >
              <InputNumber disabled={isDisabled} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Lng"
              name={['location', 'coords', 'lng']}
              rules={RULES}
            >
              <InputNumber disabled={isDisabled} />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>
      <FormSection title="Display names">
        <Row>
          <Col span={18}>
            <Form.Item label="City" name={['address', 'city']} rules={RULES}>
              <Input disabled={isDisabled} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={18}>
            <Form.Item
              label="Subcity"
              name={['address', 'subCity']}
              required={isSubCityRequired(values.address.city)}
              rules={
                isSubCityRequired(values.address.city)
                  ? [
                      { required: true },
                      {
                        pattern: /\S/,
                        message:
                          'Subcity cannot be empty or contain only spaces',
                      },
                    ]
                  : [
                      {
                        pattern: /\S/,
                        message: 'Subcity cannot contain only spaces',
                      },
                    ]
              }
            >
              <Input
                disabled={isDisabled}
                placeholder={
                  isDisabled ? undefined : 'Arrondissement or neighbourhood'
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>
      <Communication
        mode={mode}
        values={values}
        setFieldsValue={setFieldsValue}
        originalBillingEmails={originalBillingEmails}
      />
      <div className="admin_settings">
        <FormSection title="Admin settings">
          <Form.Item label="Hotel Group" name="groupId" rules={RULES}>
            <SearchBox
              dataSource={hotelGroups || []}
              placeholder={
                isDisabled
                  ? undefined
                  : 'Choose in which group the hotel will appear'
              }
              disabled={isDisabled}
            />
          </Form.Item>
          <Form.Item label="Contact BD or AM" name="managerId" rules={RULES}>
            <Select
              showSearch
              disabled={isDisabled}
              placeholder={isDisabled ? undefined : 'Select a contact'}
              optionFilterProp="children"
              filterOption={(input, option) => {
                return (
                  option?.key.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                  option?.title.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
            >
              {admins?.map((admin) => (
                <Option
                  title={`${admin.firstName} ${admin.lastName}`}
                  key={admin.email}
                  id={admin.id}
                  value={admin.id}
                >
                  {admin.firstName && admin.lastName
                    ? `${admin.firstName} ${admin.lastName} - ${admin.email}`
                    : `${admin.email}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Parent hotel Id" name="parentHotelId">
            <InputNumber min={1} disabled={isDisabled} />
          </Form.Item>
        </FormSection>
      </div>
    </Card>
  );
};
